<template>
    <div>
        <app-bar :title="event.name"/>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <div class="pt-8 pb-12 px-8">
                <div class="pb-10">
                    <v-btn
                        v-if="permitted('admin_club_management')"
                        @click="edit"
                        class="sm mr-5"
                        color="primary"
                    >{{ t('general_action_edit') }}
                    </v-btn>
                    <v-btn
                        v-if="permitted('admin_club_management')"
                        class="sm mr-5"
                        color="primary"
                        outlined
                        @click.prevent="suspend(event)"
                    >
                        {{ event.enabled ? t('general_action_suspend') : t('general_action_unsuspend') }}
                    </v-btn>
                    <confirm-modal
                        v-if="permitted('admin_club_management')"
                        :title="t('general_title_confirm_action')"
                        @agree="del(event)"
                    >
                        <template #default="{ on, attrs }">
                            <v-btn
                                class="sm"
                                color="orange"
                                outlined
                                v-on="on"
                                v-bind="attrs"
                            >{{ t('general_action_delete') }}</v-btn>
                        </template>
                    </confirm-modal>
                </div>
                <div class="float-left mr-10" style="width: 394px; max-width: 394px">
                    <img
                        v-if="event.photoUrl"
                        :src="event.photoUrl"
                        width="150"
                    />
                    <div class="form-subtitle">{{ t('club_event_label_description') }}:</div>
                    <div class="form-subtitle-description" v-html="event.description"></div>
                    <div v-if="event.link" class="form-subtitle">{{ t('club_event_label_link') }}:</div>
                    <div v-if="event.link" class="form-subtitle-description">{{ event.link }}</div>
                    <div v-if="linkParamRows().length" class="form-subtitle">{{ t('club_event_label_link_params') }}:</div>
                    <div v-if="linkParamRows().length" class="form-subtitle-description">
                        <div v-for="row in linkParamRows()" :key="row.key">{{ row.key }}: {{ row.value }}</div>
                    </div>
                    <template v-if="event.photos && event.photos.length">
                        <div class="form-subtitle mb-3">{{ t('club_event_label_header_photos') }}:</div>
                        <v-row>
                            <v-col
                                v-for="photo in event.photos"
                                :key="photo"
                                cols="6"
                            >
                                <v-sheet
                                    color="white"
                                    elevation="0"
                                    height="185"
                                    width="185"
                                    rounded="lg"
                                    class="pt-6"
                                >
                                    <v-img
                                        :lazy-src="photo"
                                        :src="photo"
                                        max-height="135"
                                        max-width="135"
                                        class="mx-auto"
                                    ></v-img>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </template>
                </div>
                <div class="float-left" style="max-width: calc(100% - 434px)">
                    <card-unit :items="items()">
                        <template #title>{{ t('club_event_label_id') }}: {{ event.id }}</template>
                    </card-unit>
                </div>
            </div>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { ClubEvent } from '@/core/models/club-event'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import CardUnit from '@/components/CardUnit.vue'

    export default defineComponent({
        components: {
            ConfirmModal,
            CardUnit
        },

        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const id = router.currentRoute.params['id']

            const state = reactive({
                overlay: true,
            })

            const capture = () => {
                store.dispatch('clubEvent/get', { id: id, payload: {} }).then(() => {
                    state.overlay = false
                })
            }

            const eventModel = computed(() => <ClubEvent>store.getters['clubEvent/get'])

            const linkParamRows = () => {
                return Object.entries(eventModel.value.linkParams || {}).map(([key, value]) => ({ key, value }))
            }

            const suspend = (item: ClubEvent) => {
                state.overlay = true
                store.dispatch('clubEvent/update', {
                    id: item.id,
                    payload: { enabled: ! item.enabled }
                }).then(() => {
                    store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                    capture()
                })
            }

            const edit = () => router.push({ name: 'club-events.show', params: { id: <any>eventModel.value.id } })

            const del = (item: ClubEvent) => {
                state.overlay = true
                store.dispatch('clubEvent/delete', { id: item.id }).then(() => {
                    router.push({ name: 'club-events' })
                })
            }

            const items = () => {
                return [
                    { text: i18n.t('club_event_label_start_at'), value: eventModel.value.startAt ? eventModel.value.startAt.format('DD/MM/YYYY HH:mm') : '-' },
                    { text: i18n.t('club_event_label_end_at'), value: eventModel.value.endAt ? eventModel.value.endAt.format('DD/MM/YYYY HH:mm') : '-' },
                    { text: i18n.t('club_event_label_location'), value: eventModel.value.location || '-' },
                    { text: i18n.t('general_action_published'), value: eventModel.value.enabled ? i18n.t('general_text_yes') : i18n.t('general_text_no') },
                ]
            }

            onMounted(() => {
                capture()
            })

            return {
                capture,
                items,
                linkParamRows,
                event: eventModel.value,
                edit,
                del,
                suspend,
                ...useI18n(),
                ...toRefs(state)
            }
        }
    })
</script>
